// Month-end ACCRUALS — the arithmetic and the draft handling behind the
// Accruals view on the Commitments tab. A schedule only: none of it reaches
// Actual, cost-to-complete or EAC.
//
// ⚠️ THE ARITHMETIC MIRRORS THE API's `accrualLineTotal` (financials/lib/accruals)
// and the two must agree. The server's value is authoritative and is what gets
// stored and returned; this exists only so the screen can total while the PM
// types. There is no shared package between the apps, so the duplication is
// real — both copies are unit-tested against the same cases.
import type { AccrualKind, AccrualLine, AccrualLineInput, AccrualTotals, AccrualUnit } from './apiClient';
import { randomId } from './randomId';

export const KIND_ORDER: readonly AccrualKind[] = ['material', 'equipment', 'subcontract'];
export const KIND_LABEL: Record<AccrualKind, string> = {
  material: 'Material', equipment: 'Equipment', subcontract: 'Subcontracts',
};
export const UNIT_LABEL: Record<AccrualUnit, string> = { day: 'Day', week: 'Week', month: 'Month' };

/** Same bound as the API: far below DECIMAL(16,2), so a stray digit is caught
 *  here rather than answered with a 400. */
export const MONEY_LIMIT = 1_000_000_000_000;

/** Largest number of units: the maximum of the API's DECIMAL(10,2) column. Held to
 *  the column, not to MONEY_LIMIT, so an oversized entry is caught here instead of
 *  failing at the database. Must match `UNITS_LIMIT` in the API. */
export const UNITS_LIMIT = 99_999_999.99;

const cents = (v: number) => Math.round(v * 100) / 100;

/** A typed amount → number, or null when the box is empty or not a number. `$`,
 *  `%`, commas and spaces are ignored, so "$12,500" and "65%" both parse. */
export function parseAmount(s: string): number | null {
  const t = String(s ?? '').replace(/[$,%\s]/g, '');
  if (t === '' || t === '-' || t === '.') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const num = (v: number | null | undefined) => (v == null || !Number.isFinite(Number(v)) ? 0 : Number(v));

/** Complete minus billed, in percent points, at the stored three-place precision. */
export function subcontractDelta(pctComplete?: number | null, pctBilled?: number | null): number {
  return Math.round((num(pctComplete) - num(pctBilled)) * 1000) / 1000;
}

/** Total accrued for one line. A blank input is 0, not 1 — nothing on this sheet
 *  is an optional multiplier. A subcontract delta can go negative (billed ahead
 *  of the work); it is shown as it falls, not clamped. */
export function accrualLineTotal(kind: AccrualKind, f: {
  carryOver?: number | null; newAccrual?: number | null;
  unitCost?: number | null; units?: number | null;
  contractValue?: number | null; pctComplete?: number | null; pctBilled?: number | null;
}): number {
  switch (kind) {
    case 'material': return cents(num(f.carryOver) + num(f.newAccrual));
    case 'equipment': return cents(num(f.unitCost) * num(f.units));
    case 'subcontract': return cents((num(f.contractValue) * subcontractDelta(f.pctComplete, f.pctBilled)) / 100);
  }
}

/**
 * The most any ONE line may total, in either direction: $100 billion. Must match
 * `LINE_TOTAL_LIMIT` in the API.
 *
 * Each input may be large on its own, but an equipment line MULTIPLIES two of
 * them, and the largest accepted pair came to ~1e20 — past where a double holds
 * cents. Bounding the total closes that, and keeps the sums below exact
 * (300 lines × 1e11 dollars × 100 cents = 3e15, inside the 9e15 a double holds).
 */
export const LINE_TOTAL_LIMIT = 100_000_000_000;

/** Section and grand totals, summed as WHOLE CENTS so they are exact: adding
 *  hundreds of decimal dollar amounts as doubles drifts, and rounding afterwards
 *  does not always bring it back. */
export function sumByKind(lines: readonly { kind: AccrualKind; total: number }[]): AccrualTotals {
  const t = { material: 0, equipment: 0, subcontract: 0 };
  for (const l of lines) t[l.kind] += Math.round(l.total * 100);
  return {
    material: t.material / 100, equipment: t.equipment / 100, subcontract: t.subcontract / 100,
    total: (t.material + t.equipment + t.subcontract) / 100,
  };
}

// ─── Drafts: the strings the PM is typing ────────────────────────────────────
// Held as text so a half-typed "12." or an empty box is not coerced mid-edit.

export interface DraftLine {
  /** React key only — never sent. */
  key: string;
  kind: AccrualKind;
  vendor: string; costCode: string; description: string;
  carryOver: string; newAccrual: string;
  unit: '' | AccrualUnit; unitCost: string; units: string;
  contractValue: string; pctComplete: string; pctBilled: string;
}

export const blankDraft = (kind: AccrualKind): DraftLine => ({
  key: randomId(), kind, vendor: '', costCode: '', description: '',
  carryOver: '', newAccrual: '', unit: '', unitCost: '', units: '',
  contractValue: '', pctComplete: '', pctBilled: '',
});

const show = (v: number | null) => (v == null ? '' : String(v));

export const draftFromLine = (l: AccrualLine): DraftLine => ({
  key: randomId(), kind: l.kind,
  vendor: l.vendor ?? '', costCode: l.costCode ?? '', description: l.description ?? '',
  carryOver: show(l.carryOver), newAccrual: show(l.newAccrual),
  unit: l.unit ?? '', unitCost: show(l.unitCost), units: show(l.units),
  contractValue: show(l.contractValue), pctComplete: show(l.pctComplete), pctBilled: show(l.pctBilled),
});

/** The parsed numeric inputs of a draft — what the arithmetic runs on. */
const parsed = (d: DraftLine) => ({
  carryOver: parseAmount(d.carryOver), newAccrual: parseAmount(d.newAccrual),
  unitCost: parseAmount(d.unitCost), units: parseAmount(d.units),
  contractValue: parseAmount(d.contractValue),
  pctComplete: parseAmount(d.pctComplete), pctBilled: parseAmount(d.pctBilled),
});

/** The live total for a draft line, from its parsed inputs. */
export const draftTotal = (d: DraftLine): number => accrualLineTotal(d.kind, parsed(d));

/** Nothing typed in any box that matters. A row the PM added and never used. */
export function isBlankDraft(d: DraftLine): boolean {
  const p = parsed(d);
  const hasAmount = d.kind === 'material' ? p.carryOver != null || p.newAccrual != null
    : d.kind === 'equipment' ? p.unitCost != null || p.units != null
    : p.contractValue != null || p.pctComplete != null || p.pctBilled != null;
  return !d.vendor.trim() && !d.costCode.trim() && !d.description.trim() && !hasAmount;
}

export type NumField = 'carryOver' | 'newAccrual' | 'unitCost' | 'units' | 'contractValue' | 'pctComplete' | 'pctBilled';

/** What each number box is called in an error message. */
const FIELD_NAME: Record<NumField, string> = {
  carryOver: 'Carry-over', newAccrual: 'Newly accrued', unitCost: 'Cost per unit', units: 'Units',
  contractValue: 'Contract value', pctComplete: 'Percent complete', pctBilled: 'Percent billed',
};
const FIELD_ORDER = Object.keys(FIELD_NAME) as NumField[];

/**
 * Why ONE box cannot be saved, or null. Lets the screen mark the offending box
 * itself, not just report a problem somewhere on the row.
 */
export function fieldProblem(d: DraftLine, f: NumField): string | null {
  const raw = d[f];
  if (raw.trim() === '') return null;
  const v = parseAmount(raw);
  const name = FIELD_NAME[f];
  if (v == null) return `${name} is not a number`;
  if (f === 'pctComplete' || f === 'pctBilled') {
    return v < 0 || v > 100 ? `${name} must be between 0 and 100` : null;
  }
  if (f === 'units') {
    if (v < 0) return 'Units cannot be negative';
    return v > UNITS_LIMIT ? `${name} is too large` : null;
  }
  return Math.abs(v) > MONEY_LIMIT ? `${name} is too large` : null;
}

/**
 * Why a draft cannot be saved, or null. Checked here so the box can be marked
 * and Save held back, instead of the PM finding out from a 400.
 */
export function draftProblem(d: DraftLine): string | null {
  for (const f of FIELD_ORDER) {
    const p = fieldProblem(d, f);
    if (p) return p;
  }
  // Each box can be fine while the line is not: an equipment line multiplies two.
  if (Math.abs(draftTotal(d)) > LINE_TOTAL_LIMIT) {
    return `A line cannot total more than $${LINE_TOTAL_LIMIT.toLocaleString('en-US')} — check the amounts and units`;
  }
  return null;
}

/**
 * The lines to post, in screen order. Blank rows are dropped here as well as on
 * the server, so a section the PM padded with spare rows costs nothing.
 *
 * Amounts are rounded to what the columns hold (cents; three places on percents)
 * because the API refuses more precision than that outright.
 */
export function draftsToPayload(drafts: readonly DraftLine[]): AccrualLineInput[] {
  const m = (s: string) => { const n = parseAmount(s); return n == null ? null : cents(n); };
  const p3 = (s: string) => { const n = parseAmount(s); return n == null ? null : Math.round(n * 1000) / 1000; };
  const text = (s: string) => s.trim() || null;
  return drafts.filter((d) => !isBlankDraft(d)).map((d) => ({
    kind: d.kind, vendor: text(d.vendor), costCode: text(d.costCode), description: text(d.description),
    carryOver: m(d.carryOver), newAccrual: m(d.newAccrual),
    unit: d.unit === '' ? null : d.unit, unitCost: m(d.unitCost), units: m(d.units),
    contractValue: m(d.contractValue), pctComplete: p3(d.pctComplete), pctBilled: p3(d.pctBilled),
  }));
}

/** Section and grand totals of a set of drafts, for the live footer. */
export const sumDrafts = (drafts: readonly DraftLine[]): AccrualTotals =>
  sumByKind(drafts.map((d) => ({ kind: d.kind, total: draftTotal(d) })));

// ─── Months ──────────────────────────────────────────────────────────────────

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/** '2026-09' → 'September 2026'. */
export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return `${MONTHS[m - 1] ?? period} ${y}`;
}

/** The month before `period`, by calendar arithmetic on the string. */
export function previousPeriod(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** The month `now` falls in, by the viewer's own clock. */
export function currentPeriod(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The months the picker offers: the last `back` months through the current one,
 * plus any older month that has saved lines, newest first. A month on file is
 * always reachable however old it is.
 */
export function periodChoices(saved: readonly string[], now: Date = new Date(), back = 12): string[] {
  const set = new Set<string>(saved);
  let p = currentPeriod(now);
  for (let i = 0; i <= back; i++) { set.add(p); p = previousPeriod(p); }
  return [...set].sort().reverse();
}
